import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Translator } from 'components/i18n';

import { Sidebar } from '../icons/Sidebar';
import { useSidebar } from '../ui/sidebar';

type SidebarTriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * When true, use sidebar-specific colors so the icon
   * blends into the left navigation. When false, use the
   * global header/background accent tokens instead.
   */
  useSidebarTheme?: boolean;
};

export default function SidebarTrigger({
  useSidebarTheme = true,
  className,
  onClick,
  ...buttonProps
}: SidebarTriggerProps) {
  const { setOpen, open, openMobile, setOpenMobile, isMobile } = useSidebar();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    isMobile ? setOpenMobile(!openMobile) : setOpen(!open);
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            id="sidebar-trigger-button"
            onClick={handleClick}
            size="icon"
            variant="ghost"
            className={cn(
              'rounded-full',
              useSidebarTheme
                ? 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                : 'text-foreground hover:bg-muted hover:text-foreground',
              className
            )}
            {...buttonProps}
          >
            <Sidebar className="!size-6" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {open ? (
              <Translator path="threadHistory.sidebar.actions.close" />
            ) : (
              <Translator path="threadHistory.sidebar.actions.open" />
            )}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
